/**
 * VoiceInputButton - 语音输入按钮组件
 *
 * 使用浏览器原生 Web Speech API（零成本，无需第三方服务）。
 * 识别语言: zh-CN，支持实时字幕预览。
 * 识别完成后通过 onTranscript 回调将文本传给父组件。
 *
 * 浏览器兼容性:
 *   ✅ Chrome / Edge (完整支持)
 *   ⚠️ Firefox (需开启 flag)
 *   ❌ Safari iOS (不支持，显示提示)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Tooltip, message } from 'antd';
import { AudioOutlined, LoadingOutlined } from '@ant-design/icons';

// Web Speech API 类型声明
interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    maxAlternatives: number;
    onstart: (event: Event) => void;
    onresult: (event: SpeechRecognitionEvent) => void;
    onerror: (event: SpeechRecognitionErrorEvent) => void;
    onend: (event: Event) => void;
    start(): void;
    stop(): void;
    abort(): void;
}

interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
    readonly length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
    readonly length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
    readonly isFinal: boolean;
}

interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message: string;
}

declare global {
    interface Window {
        SpeechRecognition: {
            new (): SpeechRecognition;
        };
        webkitSpeechRecognition: {
            new (): SpeechRecognition;
        };
    }
}

interface VoiceInputButtonProps {
    /** 识别完成后的回调，传入识别文本 */
    onTranscript: (text: string) => void;
    /** 实时识别中间结果回调（可选，用于实时预览） */
    onInterimTranscript?: (text: string) => void;
    /** 按钮是否禁用 */
    disabled?: boolean;
    /** 按钮大小 */
    size?: 'small' | 'middle' | 'large';
    /** 识别语言，默认 zh-CN */
    lang?: string;
    /** 自定义 tooltip 文字 */
    tooltip?: string;
}

const isSupported = (): boolean => {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
    onTranscript,
    onInterimTranscript,
    disabled = false,
    size = 'middle',
    lang = 'zh-CN',
    tooltip,
}) => {
    const [isListening, setIsListening] = useState(false);
    const [supported] = useState(isSupported);
    const recognitionRef = useRef<SpeechRecognition | null>(null);

    // 清理：组件卸载时停止识别
    useEffect(() => {
        return () => {
            recognitionRef.current?.abort();
        };
    }, []);

    const startListening = useCallback(() => {
        if (!supported) {
            message.warning('您的浏览器不支持语音输入，请使用 Chrome 或 Edge');
            return;
        }

        const SpeechRecognitionClass =
            window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognitionClass();

        recognition.lang = lang;
        recognition.continuous = false;
        recognition.interimResults = true;  // 实时中间结果
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            setIsListening(true);
            message.info({ content: '🎤 正在录音，请说话...', key: 'voice', duration: 0 });
        };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            // 实时预览中间结果
            if (interimTranscript && onInterimTranscript) {
                onInterimTranscript(interimTranscript);
            }

            // 最终结果回调
            if (finalTranscript) {
                onTranscript(finalTranscript);
            }
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
            setIsListening(false);
            message.destroy('voice');
            const errorMessages: Record<string, string> = {
                'no-speech': '未检测到语音，请重试',
                'audio-capture': '无法访问麦克风，请检查权限',
                'not-allowed': '麦克风权限被拒绝，请在浏览器设置中允许',
                'network': '网络错误，请检查连接',
                'aborted': '',  // 用户主动停止，不提示
            };
            const msg = errorMessages[event.error] ?? `语音识别错误: ${event.error}`;
            if (msg) message.error(msg);
        };

        recognition.onend = () => {
            setIsListening(false);
            message.destroy('voice');
        };

        recognitionRef.current = recognition;
        recognition.start();
    }, [supported, lang, onTranscript, onInterimTranscript]);

    const stopListening = useCallback(() => {
        recognitionRef.current?.stop();
        setIsListening(false);
        message.destroy('voice');
    }, []);

    const handleClick = () => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    };

    if (!supported) {
        return (
            <Tooltip title="此浏览器不支持语音输入，请使用 Chrome 或 Edge">
                <Button
                    icon={<AudioOutlined />}
                    size={size}
                    disabled
                    style={{ color: '#ccc' }}
                />
            </Tooltip>
        );
    }

    const tooltipText = tooltip || (isListening ? '点击停止录音' : '点击开始语音输入');

    return (
        <Tooltip title={tooltipText}>
            <Button
                icon={
                    isListening ? (
                        <LoadingOutlined style={{ color: '#ff4d4f' }} />
                    ) : (
                        <AudioOutlined />
                    )
                }
                size={size}
                onClick={handleClick}
                disabled={disabled}
                danger={isListening}
                style={{
                    transition: 'all 0.3s',
                    ...(isListening && {
                        animation: 'pulse 1.5s ease-in-out infinite',
                        boxShadow: '0 0 0 0 rgba(255, 77, 79, 0.4)',
                    }),
                }}
            />
        </Tooltip>
    );
};

export default VoiceInputButton;
